export type PermissionScope = "assigned" | "all";
export type PermissionGrants = Record<string, PermissionScope>;
export type PermissionOverrides = Record<string, PermissionScope | "deny">;
export type CanAccess = (permission: string) => boolean;

export const accountPagePermissions: Record<string, string> = {
  access: 'access.manage', users: 'users.invite', profiles: 'profiles.view', partners: 'partners.view',
  files: 'clinic_files.view', 'promo-codes': 'promo_codes.view', recruitment: 'recruitment.view',
  'verification-queue': 'verification.view', 'activation-engagement': 'engagement.view', recruiters: 'recruiters.view',
};

// Page access and action access are distinct. The API remains authoritative for record scope.
export const pagePermission = (pathname: string): string => {
  const path = pathname.replace(/^\//, "");
  if (path === "account-management" || path.startsWith("account-management/")) return accountPagePermissions[path.split('/')[1]] || 'access.manage';
  if (path.startsWith("settings/admin/resource-management")) return "resources.view";
  if (path.startsWith("settings/")) return "access.manage";
  if (path.startsWith("dashboard/admin")) return "access.manage";
  if (path.startsWith("dashboard/")) return "reports.view";
  if (path.startsWith("queue")) return "queue.view";
  if (/^appointments\/(slots|slot-templates)/.test(path)) return "appointments.slots";
  if (path.startsWith("appointments")) return path.includes("masterclass") ? "resources.view" : "appointments.view";
  if (path.startsWith("patients")) {
    if (path === "patients/create") return "patients.create";
    if (path.endsWith("/medication")) return "medications.view";
    if (path.endsWith("/consultation/general")) return "consultations.create";
    const section = path.split("/")[2];
    const areas: Record<string, string> = {
      consultation: "consultations", "general-consultation": "consultations", timeline: "consultations",
      laboratory: "labs", radiology: "radiology", prescription: "prescriptions",
      immunization: "preventative", billing: "billing", records: "consultations", notes: "notes",
    };
    return `${areas[section] || "patients"}.view`;
  }
  return "access.manage";
};

export const hasPageAccess = (grants: PermissionGrants, pathname: string): boolean => {
  if (/^\/?account-management\/?$/.test(pathname)) {
    return Object.values(accountPagePermissions).some(permission => Boolean(grants[permission]));
  }
  return Boolean(grants[pagePermission(pathname)]);
};

export const notifyPermissionChange = () => window.dispatchEvent(new Event("permissions-changed"));

export const firstAccessiblePath = (grants: PermissionGrants) =>
  ["/patients", "/appointments", "/queue", "/dashboard/staff", "/account-management"]
    .find(path => hasPageAccess(grants, path)) || "/patients";
